/**
 * PropertyFilter query → GetPipelineBubbles `pipelineFilter` input.
 *
 * The Impact page's Pipeline类型 slot filters opps by their pipeline attributes
 * (SFDC Stage / Engagement Depth Score / Create Date / Close Date / Big Bet /
 * Greenfield / cross-industry attribution /
 * IVT直接参与) through a Cloudscape PropertyFilter with one-level AND/OR
 * grouping (`enableTokenGroups`). This module translates the control's
 * `PropertyFilterProps.Query` into the backend's typed tree.
 *
 * WIRE: the backend input `pipelineFilter` is a **JSON string** on
 * @httpQuery("pipelineFilter") (GetPipelineBubbles is a GET; a query string
 * can't carry a nested structure — same encoding as `sfdcPipelinePropertyFilter`
 * on ListTaggedPipelines, from which this module is forked). So
 * `queryToImpactFilter` returns the logical object (or undefined), and the
 * caller JSON.stringify's it into the input. Splitting it this way keeps this
 * function unit-testable on the object, not a string.
 *
 * Backing backend code: `filter.PropertyFilterEngine` (parse + normalize, size caps) over
 * `filter.FieldRegistries.IMPACT`, which is the authoritative field allowlist and per-field
 * value shape. (This used to name `entity.ImpactPipelineFilter`; that class is gone — the two
 * pages' filters were unified onto the engine + registries.)
 *
 * See design 2026-07-31-impact-property-filter-design.
 */

import type { PropertyFilterProps } from '@amzn/awsui-components-react';

/** Backend field ids (FieldRegistries.IMPACT). Values MUST match the
 *  PropertyFilter `filteringProperties[].key` so a token's propertyKey maps
 *  straight through. Impact-scoped superset of the Motion Pipeline tab's
 *  SfdcPipelineFilterField — adds date-range + boolean-flag dimensions, drops
 *  the identity fields (ACCOUNT_NAME / OPP_NAME / OPP_ID / OPP_OWNER) that don't
 *  apply to the bubble aggregate. */
export type ImpactPipelineFilterField =
  | 'SFDC_STAGE'
  | 'PIPELINE_SCORE'
  | 'CREATED_DATE'
  | 'CLOSE_DATE'
  | 'IS_BIG_BET'
  | 'HAS_IVT_ACTIVITY'
  | 'IS_GREENFIELD'
  | 'TAG_MATCHES_HIERARCHY';

const KNOWN_FIELDS: ReadonlySet<string> = new Set<ImpactPipelineFilterField>([
  'SFDC_STAGE',
  'PIPELINE_SCORE',
  'CREATED_DATE',
  'CLOSE_DATE',
  'IS_BIG_BET',
  'HAS_IVT_ACTIVITY',
  'IS_GREENFIELD',
  'TAG_MATCHES_HIERARCHY',
]);

/** Boolean fields that accept EXACTLY ONE of "true"/"false". Selecting both (or
 *  none) means "no constraint" — and the backend's `ValueKind.BOOL.needsSingleValue()`
 *  is always true, so a multi-value BOOL is a 400; we drop the condition rather than
 *  emit values:["true","false"].
 *
 *  ⚠️ This must list EVERY BOOL field, not just the newest one. It previously held
 *  IS_GREENFIELD alone, with a note that IS_BIG_BET / HAS_IVT_ACTIVITY "pre-date this
 *  guard" — but the guard mirrors a BACKEND rule that applies to them too, so ticking
 *  both boxes on those two 400'd. The sibling module
 *  (utils/sfdcPipelinePropertyFilter.ts) already listed all three; this one was the
 *  outlier. The symptom of a missing entry is a 400, not a silent downgrade. */
const BOOL_SINGLE_VALUE_FIELDS: ReadonlySet<string> = new Set([
  'IS_GREENFIELD',
  'IS_BIG_BET',
  'HAS_IVT_ACTIVITY',
  'TAG_MATCHES_HIERARCHY',
]);

/** in / notIn: set membership on SFDC_STAGE / PIPELINE_SCORE / IS_BIG_BET /
 *  HAS_IVT_ACTIVITY (and, for point-in-time equality, on date fields).
 *  lt/lte/gt/gte: scalar comparison on date fields only — backend enforces this
 *  and rejects on other fields with a 400. */
export type ImpactPipelineFilterOperator = 'in' | 'notIn' | 'lt' | 'lte' | 'gt' | 'gte';

const DATE_FIELDS: ReadonlySet<ImpactPipelineFilterField> = new Set(['CREATED_DATE', 'CLOSE_DATE']);

export interface PropertyFilterCondition {
  field: ImpactPipelineFilterField;
  operator: ImpactPipelineFilterOperator;
  values: string[];
}

export interface PropertyFilterGroup {
  operation: 'and' | 'or';
  conditions: PropertyFilterCondition[];
}

export interface ImpactPipelineFilter {
  operation: 'and' | 'or';
  groups: PropertyFilterGroup[];
}

// A PropertyFilter token or (one level of) nesting. Mirrors the collection-hooks
// union; typed locally because the query we read may carry either shape under
// tokenGroups when enableTokenGroups is on.
type Token = PropertyFilterProps.Token;
type TokenOrGroup = PropertyFilterProps.Token | PropertyFilterProps.TokenGroup;

function isGroup(node: TokenOrGroup): node is PropertyFilterProps.TokenGroup {
  return Array.isArray((node as PropertyFilterProps.TokenGroup).tokens);
}

/** enum-token value → string[]. Cloudscape stores an enum token's value as an
 *  array (utils.js castValue), but be defensive: a scalar becomes a single-item
 *  array; null/undefined → []. Non-string entries are coerced then dropped if empty. */
function toValues(value: unknown): string[] {
  const raw = value == null ? [] : Array.isArray(value) ? value : [value];
  return raw
    .filter((v) => v != null)
    .map((v) => String(v))
    .filter((v) => v.length > 0);
}

/** Cloudscape operator string → backend operator. Set operators (=/!=) map to
 *  in/notIn on every field. Comparison operators (<, <=, >, >=) only make sense
 *  on date fields — backend rejects otherwise; we mirror by dropping any non-
 *  date condition that carries a comparison operator. Any other operator
 *  (unexpected: this control is configured with a closed operator set) → null,
 *  condition dropped. */
function toOperator(
  op: string | undefined,
  field: ImpactPipelineFilterField,
): ImpactPipelineFilterOperator | null {
  if (op === '=') return 'in';
  if (op === '!=') return 'notIn';
  if (!DATE_FIELDS.has(field)) return null;
  switch (op) {
    case '<':
      return 'lt';
    case '<=':
      return 'lte';
    case '>':
      return 'gt';
    case '>=':
      return 'gte';
    default:
      return null;
  }
}

/** One token → one condition, or null if it can't be represented (unknown field,
 *  unsupported operator for this field, or empty values → drop). */
function tokenToCondition(t: Token): PropertyFilterCondition | null {
  const key = t.propertyKey;
  if (!key || !KNOWN_FIELDS.has(key)) return null;
  const field = key as ImpactPipelineFilterField;
  const operator = toOperator(t.operator, field);
  if (!operator) return null;
  const values = toValues(t.value);
  if (values.length === 0) return null;
  // BOOL single-value field: the backend enforces exactly one of
  // "true"/"false". Both selected (values.length === 2) means "no constraint" —
  // drop the condition rather than send values:["true","false"] (→ 400). Exactly
  // one value passes through as-is.
  if (BOOL_SINGLE_VALUE_FIELDS.has(field) && values.length !== 1) return null;
  // Backend rejects >1 value on date comparison operators; strip extras client-
  // side so the request doesn't 400. First value wins (Cloudscape only ever
  // emits one on a scalar operator token anyway).
  const isDateComparison =
    DATE_FIELDS.has(field) && (operator === 'lt' || operator === 'lte' || operator === 'gt' || operator === 'gte');
  const finalValues = isDateComparison && values.length > 1 ? [values[0]] : values;
  return { field, operator, values: finalValues };
}

/**
 * Translate a PropertyFilter query into the backend filter tree.
 * Returns `undefined` when the query carries no usable condition (so the caller
 * omits the param — the backend rejects an empty string via @pattern("^\\{")).
 *
 * Reads `tokenGroups` when present (enableTokenGroups=true); each top-level entry
 * that is a bare token becomes a single-condition group, and each nested group
 * becomes a multi-condition group. Falls back to flat `tokens` otherwise. The
 * backend supports exactly one level of nesting (Cloudscape can't emit deeper),
 * so we flatten any (unexpected) deeper group by lifting its tokens into the
 * current group rather than recursing.
 */
export function queryToImpactFilter(
  query: PropertyFilterProps.Query,
): ImpactPipelineFilter | undefined {
  const topOperation: 'and' | 'or' = query.operation === 'or' ? 'or' : 'and';

  const nodes: readonly TokenOrGroup[] =
    query.tokenGroups && query.tokenGroups.length > 0 ? query.tokenGroups : query.tokens ?? [];

  const groups: PropertyFilterGroup[] = [];

  for (const node of nodes) {
    if (isGroup(node)) {
      const groupOp: 'and' | 'or' = node.operation === 'or' ? 'or' : 'and';
      const conditions: PropertyFilterCondition[] = [];
      for (const child of node.tokens ?? []) {
        if (isGroup(child)) {
          for (const leaf of child.tokens ?? []) {
            if (!isGroup(leaf)) {
              const c = tokenToCondition(leaf);
              if (c) conditions.push(c);
            }
          }
        } else {
          const c = tokenToCondition(child);
          if (c) conditions.push(c);
        }
      }
      if (conditions.length > 0) groups.push({ operation: groupOp, conditions });
    } else {
      const c = tokenToCondition(node);
      if (c) groups.push({ operation: 'and', conditions: [c] });
    }
  }

  if (groups.length === 0) return undefined;
  return { operation: topOperation, groups };
}

/** Convenience for the caller: the JSON string the backend expects, or undefined. */
export function queryToImpactFilterParam(query: PropertyFilterProps.Query): string | undefined {
  const tree = queryToImpactFilter(query);
  return tree ? JSON.stringify(tree) : undefined;
}
